import type { User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { CurrentUserProfile, FeaturePermissionKey, Role } from '@/types'
import { FEATURE_PERMISSION_KEYS, SCREEN_PERMISSION_KEYS } from '@/types'

/** Resolve nome do user a partir dos metadados Google OAuth (fallback chain). */
export function resolveUserName(user: User | null): string | null {
  if (!user) return null
  const meta = user.user_metadata ?? {}
  const candidate =
    (meta.full_name as string | undefined) ||
    (meta.name as string | undefined) ||
    (meta.display_name as string | undefined) ||
    (meta.preferred_username as string | undefined) ||
    (user.email ? user.email.split('@')[0] : null)
  return candidate?.trim() || null
}

/**
 * Carrega o CurrentUserProfile completo (base + extension + tenant + campaigns
 * visíveis + permissions + flag platform_admin) pra um userId específico.
 *
 * Platform admin tem tratamento especial:
 *  - Constrói team virtual com role='admin' (não está em team_members por design)
 *  - Recebe TODAS as permissions ligadas (ignora role_permissions do DB) — evita
 *    bug onde key órfão bloqueia aba até pro admin
 */
export async function loadOwnProfile(userId: string): Promise<CurrentUserProfile | null> {
  // F2 (S6-03): distinguir "sem perfil" (row ausente → denied definitivo) de
  // "falha de infra" (PostgREST 5xx/timeout/RLS mal aplicada → transiente). Antes
  // qualquer erro virava `data=null`, que o AuthContext interpreta como "Perfil
  // não registrado. Contate o admin." — trancando usuário legítimo num blip de
  // rede, sem nada no Sentry. Nas queries críticas de identidade (users, tenants),
  // propagamos o erro: o AuthContext o captura (Sentry + "Erro carregando perfil"
  // transiente), em vez de negar acesso como se o perfil não existisse.
  const { data: baseUser, error: baseUserErr } = await supabase.from('users').select('*').eq('id', userId).maybeSingle()
  if (baseUserErr) throw baseUserErr
  if (!baseUser) return null

  const { data: tenant, error: tenantErr } = await supabase
    .from('tenants').select('*').eq('id', baseUser.tenant_id).maybeSingle()
  if (tenantErr) throw tenantErr
  if (!tenant) { console.warn('[auth] tenant not found', baseUser.id); return null }

  // Platform admin check — precisa ser feito cedo pra construir team virtual
  const { data: paRow } = await supabase.from('platform_admins')
    .select('user_id').eq('user_id', baseUser.id).maybeSingle()
  const isPlatformAdmin = !!paRow

  let team: CurrentUserProfile['team']
  let client: CurrentUserProfile['client']
  if (baseUser.user_type === 'team_member') {
    if (isPlatformAdmin) {
      // God mode: platform admin entrou num tenant, mas não está registrado
      // em team_members (por design). Construimos um team virtual com
      // role='admin' pro tenant atual. UI não precisa saber a diferença.
      team = {
        user_id: baseUser.id,
        tenant_id: baseUser.tenant_id,
        role: 'admin',
        leader_id: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as CurrentUserProfile['team']
    } else {
      const { data } = await supabase.from('team_members')
        .select('*').eq('user_id', baseUser.id).maybeSingle()
      team = data || undefined
    }
  } else {
    const { data } = await supabase.from('client_profiles')
      .select('*, client_ref:clients(*)').eq('user_id', baseUser.id).maybeSingle()
    client = (data as CurrentUserProfile['client']) || undefined
  }

  let campaignIds: string[] = []
  if (baseUser.user_type === 'team_member' && team) {
    if (isPlatformAdmin || team.role === 'admin' || team.role === 'manager') {
      // Platform admin ou admin/manager: vê todas as campaigns do tenant atual
      const { data } = await supabase.from('campaigns').select('id').eq('tenant_id', baseUser.tenant_id)
      campaignIds = (data || []).map((c: { id: string }) => c.id)
    } else {
      const { data } = await supabase.from('team_member_campaigns')
        .select('campaign_id').eq('team_member_id', baseUser.id)
      campaignIds = (data || []).map((r: { campaign_id: string }) => r.campaign_id)
    }
  } else if (baseUser.user_type === 'client_user') {
    const { data } = await supabase.from('client_user_campaigns')
      .select('campaign_id').eq('client_user_id', baseUser.id)
    campaignIds = (data || []).map((r: { campaign_id: string }) => r.campaign_id)
  }

  const role: Role = team?.role || 'client'
  const permissions: Partial<Record<FeaturePermissionKey, boolean>> = {}
  // Godmode: admin (tenant) e platform admin veem TUDO, sempre. Ignora DB —
  // garante que features/telas novas não precisem de migration pra aparecer pro
  // admin, e evita bug visto em prod onde `ver_pagamentos_aba` ficou órfão no
  // role_permissions e bloqueou a aba até pro admin.
  if (isPlatformAdmin || role === 'admin') {
    for (const k of FEATURE_PERMISSION_KEYS) permissions[k] = true
    for (const k of SCREEN_PERMISSION_KEYS) permissions[k] = true
  } else {
    const { data: perms } = await supabase.from('role_permissions')
      .select('permission_key, granted').eq('role', role).eq('tenant_id', baseUser.tenant_id)
    ;(perms || []).forEach((p: { permission_key: string; granted: boolean }) => {
      permissions[p.permission_key as FeaturePermissionKey] = p.granted
    })
  }

  return {
    user: baseUser, tenant, team, client,
    campaign_ids: campaignIds, permissions,
    is_platform_admin: isPlatformAdmin,
  }
}
